import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { initGA, usePageTracking } from './utils/analytics';
import Navbar from './components/Navbar';
import SimpleLoader from './components/SimpleLoader';
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';

import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import RentItems from './pages/RentItems';
import ListItems from './pages/ListItems';
import Dashboard from './pages/Dashboard';
import Membership from './pages/Membership';
import AdminDashboard from './pages/AdminDashboard';
import Landing from './pages/Landing';
import Chat from './pages/Chat';
import ListingDetails from './pages/ListingDetails';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/ContactUs';
import CancellationRefund from './pages/CancellationRefund';
import AdminSetup from './pages/AdminSetup';
import PublicBrowse from './components/PublicBrowse';
// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1EB' }}>
        <SimpleLoader size="xl" text="Checking authentication..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.isAdmin) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1EB' }}>
        <SimpleLoader size="xl" text="Loading..." />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

function App() {
  useEffect(() => {
    // Initialize Google Analytics
    initGA();

    // Suppress ResizeObserver errors globally
    const handleError = (event) => {
      if (event.message && (
        event.message.includes('ResizeObserver loop limit exceeded') ||
        event.message.includes('ResizeObserver loop completed with undelivered notifications')
      )) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason && event.reason.message &&
        event.reason.message.includes('ResizeObserver')) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

// Separate component to use analytics hooks inside AuthProvider
function AppContent() {
  // Enable automatic page tracking
  usePageTracking();

  return (
    <div className="App">
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/cancellation-refund" element={<CancellationRefund />} />
        <Route path="/admin-setup" element={<AdminSetup />} />
        <Route path="/browse" element={<PublicBrowse />} />
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/signup" element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        } />

        {/* Protected Routes */}
        <Route path="/home" element={
          <ProtectedRoute>
            <Navbar />
            <Home />
          </ProtectedRoute>
        } />
        <Route path="/rent-items" element={
          <ProtectedRoute>
            <Navbar />
            <RentItems />
          </ProtectedRoute>
        } />
        <Route path="/list-items" element={
          <ProtectedRoute>
            <Navbar />
            <ListItems />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Navbar />
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/membership" element={
          <ProtectedRoute>
            <Navbar />
            <Membership />
          </ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute>
            <Navbar />
            <Chat />
          </ProtectedRoute>
        } />
        <Route path="/listing/:id" element={
          <ProtectedRoute>
            <Navbar />
            <ListingDetails />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly={true}>
            <Navbar />
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ScrollToTopButton />
    </div>
  );
}

export default App;